import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { queryClient } from "App";
import { FirmCardDropDown } from "app/components/common/FirmCardDropDown";
import PaymentModeComponent from "app/components/common/PaymentModeComponent";
import StaffSearchInput from "app/components/common/StaffSearchInput";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";
import { selectActiveSession } from "app/modules/schools/selectors";
import {
  addCompletedExpenseDetail,
  getAllExpenseHead,
  getAllExpenseSubHead,
} from "app/services/ledgerAccount";
import { getAllFirm } from "app/services/schoolService/firm.service";
import { getTransport } from "app/services/student-management";
import { MenuProps, ValidateFileSize } from "app/utils/helper";
import {
  CustomSelectBox,
  CustomTextArea,
  CustomTextField,
} from "assets/styles/globalStyledComponent";

// ============ Styled Components ============
const Label = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: "1.2rem",
  marginBottom: "10px",
  fontWeight: 500,
  color: "#0C2F49",
}));

// ============ Helper Components ============
const StaffCard = ({ item }) => {
  return (
    <Box
      sx={{
        border: "1px solid #E0E7FF",
        borderRadius: "12px",
        padding: "12px 16px",
        backgroundColor: "#F8FAFF",
        boxShadow: "0px 2px 8px rgba(61, 57, 137, 0.08)",
        transition: "all 0.2s ease-in-out",
        minWidth: "280px",
        maxWidth: "400px",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            position: "relative",
            width: "50px",
            height: "50px",
            borderRadius: "12px",
            overflow: "hidden",
            border: "2px solid #fff",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <img
            src={
              item?.profilePic ? item?.profilePic : "/image-placeholder.jpeg"
            }
            alt="staff"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
        <Stack flex={1} spacing={0.5}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "15px",
              color: "#1E293B",
              lineHeight: "1.3",
            }}
          >
            {item?.name}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Box
              sx={{
                backgroundColor: "#EEF2FF",
                borderRadius: "6px",
                padding: "2px 8px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#4F46E5",
                }}
              >
                {item?.departmentName || "--"}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: "#FEF3C7",
                borderRadius: "6px",
                padding: "2px 8px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#92400E",
                }}
              >
                {item?.designation || "--"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

// ============ Validation Schema ============
const getValidationSchema = () =>
  Yup.object({
    head: Yup.mixed().required("Head Selection is required"),
    subHead: Yup.mixed().required("Sub Head Selection is required"),
    vehicle: Yup.string().when("isVehicle", {
      is: true,
      then: Yup.string().required("Vehicle Selection is required"),
    }),
    amt: Yup.number().required("Amount is required"),
    purpose: Yup.string().required("Purpose is required"),
    type: Yup.string().required("Type is required"),
    dept: Yup.mixed().when("type", {
      is: (val) => val === "Staff",
      then: Yup.mixed().required("Department is required"),
      otherwise: Yup.mixed(),
    }),
    name: Yup.string().when("type", {
      is: (val) => val === "Staff",
      then: Yup.string().required("Name is required"),
      otherwise: Yup.string(),
    }),
    fName: Yup.string().when("type", {
      is: (val) => val === "Other",
      then: Yup.string().required("Firm Name is required"),
      otherwise: Yup.string(),
    }),
  });

// ============ Main Component ============
const CompletedExpenseForm = ({ dialogs, onSuccess, onOpenSubHeadDialog }) => {
  const selectedSession = useSelector(selectActiveSession);
  const { handleUploadImage } = useFolderWiseFileUploader();
  const paymentDetailsRef = useRef(null);

  // ============ State Management ============
  const [state, setState] = useState({
    image: null,
    firms: [],
    viaStaffDetail: {},
    recipientPersonDetail: {},
  });

  // ============ Form ============
  const formik = useFormik({
    initialValues: {
      subHeads: [],
      heads: [],
      head: "",
      subHead: "",
      vehicle: "",
      type: "Staff",
      date: new Date(),
      dept: "",
      name: "",
      fName: "",
      amt: "",
      purpose: "",
      isVehicle: false,
      transportList: [],
      description: "",
      paymentDetails: null,
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      // Use payment details from ref if available, otherwise from formik values
      const finalValues = {
        ...values,
        paymentDetails: paymentDetailsRef.current || values.paymentDetails,
      };
      handleUploadServer(finalValues);
    },
  });

  // ============ Handlers ============
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const { valid, message } = ValidateFileSize(file, {
      validateImage: true,
      validatePDF: true,
      imageLimit: 0.5 * 1048576, // 0.5 MB
    });

    if (!valid) {
      toast.error(message);
      return;
    }

    setState((prev) => ({
      ...prev,
      image: {
        file: file,
        link: URL.createObjectURL(file),
      },
    }));
  };

  const handleUploadServer = async (values) => {
    try {
      let attachmentUrl = state.image?.file
        ? await handleUploadImage(
            true,
            state.image.file,
            "withSession",
            "LEDGER",
            "EXPENSES"
          )
        : { fileURL: null };

      const payload = {
        session: selectedSession,
        expenseSubHeadDocId: values.subHead,
        ...(attachmentUrl && {
          attachmentUrl: attachmentUrl?.fileURL,
        }),
        vehicleNumber: values.vehicle,
        purpose: values.purpose,
        recipientPersonRole: values.type,
        recipientPersonDocId:
          values.type === "Staff" ? values.name : values.fName,
        expenseDate: values.date,
        paymentDetails: values.paymentDetails,
        ...(state.viaStaffDetail?.staffDocId && {
          viaPersonDocId: state.viaStaffDetail?.staffDocId,
        }),
      };
      addCompletedExpenseDetailMutate(payload);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleViaStaff = (payload) => {
    setState((prev) => ({ ...prev, viaStaffDetail: payload }));
  };

  const handleRecipientPerson = (payload) => {
    setState((prev) => ({ ...prev, recipientPersonDetail: payload }));
    if (payload?.staffDocId) {
      formik.setFieldValue("name", payload.staffDocId);
      formik.setFieldValue("type", "Staff");
      formik.setFieldValue(
        "dept",
        payload.departmentName || payload.department || "N/A"
      );
    }
  };

  const removeViaStaff = () => {
    setState((prev) => ({ ...prev, viaStaffDetail: {} }));
  };

  const removeRecipientPerson = () => {
    setState((prev) => ({ ...prev, recipientPersonDetail: {} }));
    formik.setFieldValue("name", "");
  };

  // ============ API Mutations ============
  const { mutate: addCompletedExpenseDetailMutate } = useMutation(
    addCompletedExpenseDetail,
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message);
        queryClient.invalidateQueries({ queryKey: "getAllExpense" });
        onSuccess();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message);
      },
    }
  );

  // ============ API Queries ============
  useQuery({
    queryKey: ["getTransport"],
    queryFn: () => getTransport({ status: "ACTIVE" }),
    onSuccess: (res) => {
      const transportList =
        res?.data?.list?.map((item) => item.vehicleNumber) || [];
      formik.setValues((prev) => ({
        ...prev,
        transportList: transportList,
      }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: formik.values.isVehicle,
  });

  useQuery({
    queryKey: ["getAllExpenseHead", selectedSession],
    queryFn: getAllExpenseHead,
    onSuccess: (res) => {
      const heads =
        res?.data?.list?.map((item) => ({
          id: item._id,
          name: item.expenseHeadName,
        })) || [];

      formik.setValues((prev) => ({
        ...prev,
        ...(dialogs?.subHead && { subHead: dialogs.subHead }),
        ...(dialogs?.headId && { head: dialogs.headId }),
        heads: heads,
      }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: dialogs?.add ? true : false,
  });

  useQuery({
    queryKey: ["getAllExpenseSubHeadForCom", formik.values.head],
    queryFn: () =>
      getAllExpenseSubHead({ expenseHeadDocId: formik.values.head }),
    onSuccess: (res) => {
      formik.setValues((prev) => ({
        ...prev,
        subHeads:
          res?.data?.list?.map((item) => ({
            id: item._id,
            name: item.expenseSubHeadName,
            description: item.description,
          })) || [],
      }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: formik.values.head ? true : false,
  });

  useQuery({
    queryKey: "getAllFirm",
    queryFn: () => getAllFirm({}),
    enabled: formik.values.type === "Other",
    onSuccess: (res) => {
      setState((prev) => ({ ...prev, firms: res?.data?.list || [] }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  // ============ Render ============
  return (
    <>
      <Box>
        <Grid container rowSpacing={1.5} columnSpacing={1} mt={1}>
          {/* Expense Head */}
          <Grid item xs={6} md={4}>
            <Label>Expense Head</Label>
            <CustomSelectBox
              name="head"
              fullWidth
              size="small"
              value={formik.values.head}
              displayEmpty
              MenuProps={MenuProps}
              onChange={(e) => {
                formik.handleChange(e);
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {formik.values.heads?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </CustomSelectBox>
            {formik.touched.head && formik.errors.head && (
              <Typography sx={{ color: "red" }}>
                {formik.errors.head}
              </Typography>
            )}
          </Grid>

          {/* Sub Head */}
          <Grid item xs={6} md={4}>
            <Label>Sub Head</Label>
            <CustomSelectBox
              name="subHead"
              disabled={!formik.values.head}
              fullWidth
              size="small"
              displayEmpty
              value={formik.values.subHead}
              MenuProps={MenuProps}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {formik.values.subHeads?.map((item) => (
                <MenuItem
                  key={item.id}
                  value={item?.id}
                  onClick={() =>
                    formik.setValues((prev) => ({
                      ...prev,
                      subHead: item.id,
                      description: item?.description,
                    }))
                  }
                >
                  {item.name}
                </MenuItem>
              ))}
              <MenuItem
                sx={{
                  color: "blue",
                  textDecoration: "underline",
                  fontStyle: "italic",
                }}
                value="Add New"
                onClick={() => {
                  onOpenSubHeadDialog(formik.values.head);
                  formik.setValues((prev) => ({
                    ...prev,
                    subHead: "",
                  }));
                }}
              >
                Add New
              </MenuItem>
            </CustomSelectBox>
            {formik.touched.subHead && formik.errors.subHead && (
              <Typography sx={{ color: "red" }}>
                {formik.errors.subHead}
              </Typography>
            )}
          </Grid>

          {/* Vehicle Expense */}
          <Grid item xs={6} md={4}>
            <Stack direction="row" alignItems="center">
              <Label>Vehicle Expense ?</Label>
              <Stack direction="row" alignItems="center" sx={{ mt: "-10px" }}>
                <Checkbox
                  checked={formik.values.isVehicle}
                  onChange={(e) =>
                    formik.setValues((prev) => ({
                      ...prev,
                      isVehicle: !prev.isVehicle,
                    }))
                  }
                />
                <Typography>Yes</Typography>
              </Stack>
            </Stack>
            {formik.values.isVehicle && (
              <>
                <CustomSelectBox
                  fullWidth
                  size="small"
                  displayEmpty
                  name="vehicle"
                  value={formik.values.vehicle}
                  MenuProps={MenuProps}
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                >
                  <MenuItem value="" disabled>
                    Select
                  </MenuItem>
                  {formik.values.transportList?.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </CustomSelectBox>
                {formik.touched.vehicle && formik.errors.vehicle && (
                  <Typography sx={{ color: "red" }}>
                    {formik.errors.vehicle}
                  </Typography>
                )}
              </>
            )}
          </Grid>

          {/* Sub Head Description */}
          <Grid item xs={12}>
            <Label>Sub Head Description</Label>
            <Typography>{formik.values.description || "--"}</Typography>
          </Grid>

          {/* Via Staff Selection */}
          <Grid item xs={12}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              gap={2}
              alignItems={{ xs: "flex-start", md: "flex-start" }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: "200px" }}>
                <TextFieldLabel title={"Via"} />
                <StaffSearchInput returnResponse={handleViaStaff} />
              </Box>

              {Object.keys(state.viaStaffDetail).length > 0 && (
                <Stack
                  direction="row"
                  gap={1.5}
                  alignItems="center"
                  sx={{ ml: { xs: 0, md: "auto" } }}
                >
                  <StaffCard item={state.viaStaffDetail} />
                  <IconButton
                    sx={{
                      background: "#FEE2E2",
                      width: "36px",
                      height: "36px",
                    }}
                    onClick={removeViaStaff}
                  >
                    <Close sx={{ color: "#DC2626", fontSize: "20px" }} />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Grid>

          {/* Recipient Person Type */}
          <Grid item xs={6} md={4}>
            <Label>Recipient Person Type</Label>
            <CustomSelectBox
              name="type"
              fullWidth
              size="small"
              value={formik.values.type}
              onChange={(e) => {
                formik.handleChange(e);
                if (
                  e.target.value !== state.recipientPersonDetail?.staffDocId
                ) {
                  setState((prev) => ({
                    ...prev,
                    recipientPersonDetail: {},
                  }));
                }
              }}
              displayEmpty
              MenuProps={MenuProps}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              <MenuItem value="Staff">Staff</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </CustomSelectBox>
            {formik.touched.type && formik.errors.type && (
              <Typography sx={{ color: "red" }}>
                {formik.errors.type}
              </Typography>
            )}
          </Grid>

          {/* Firm Name - for Other type */}
          {formik.values.type === "Other" && (
            <Grid item xs={6} md={4}>
              <Label>Firm Name</Label>
              <CustomSelectBox
                fullWidth
                size="small"
                MenuProps={MenuProps}
                name="fName"
                displayEmpty
                value={formik.values.fName}
                onChange={formik.handleChange}
                renderValue={
                  formik.values.fName
                    ? () => {
                        const item = state.firms?.find(
                          (item) => item._id === formik.values.fName
                        );
                        return item?.firmName;
                      }
                    : null
                }
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {state?.firms?.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    <FirmCardDropDown item={item} />
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {formik.touched.fName && formik.errors.fName && (
                <Typography sx={{ color: "red" }}>
                  {formik.errors.fName}
                </Typography>
              )}
            </Grid>
          )}

          {/* Recipient Person - for Staff type */}
          {formik.values.type === "Staff" && (
            <Grid item xs={12} md={8}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                gap={2}
                alignItems={{ xs: "flex-start", md: "flex-start" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: "200px" }}>
                  <TextFieldLabel title={"Recipient Person"} />
                  <StaffSearchInput returnResponse={handleRecipientPerson} />
                </Box>
              </Stack>
            </Grid>
          )}
          {Object.keys(state.recipientPersonDetail).length > 0 && (
            <Grid item xs={12} md={9}>
              <Stack
                direction="row"
                gap={1.5}
                alignItems="center"
                sx={{ ml: { xs: 0, md: "auto" } }}
              >
                <StaffCard item={state.recipientPersonDetail} />
                <IconButton
                  sx={{
                    background: "#FEE2E2",
                    width: "36px",
                    height: "36px",
                  }}
                  onClick={removeRecipientPerson}
                >
                  <Close sx={{ color: "#DC2626", fontSize: "20px" }} />
                </IconButton>
              </Stack>
            </Grid>
          )}

          {/* Purpose */}
          <Grid item xs={12}>
            <Label>Purpose</Label>
            <CustomTextArea
              placeholder="Enter Purpose"
              name="purpose"
              value={formik.values.purpose}
              onChange={formik.handleChange}
            />

            {/* <CustomTextField
              fullWidth
              size="small"
              placeholder="Enter Purpose"
              name="purpose"
              value={formik.values.purpose}
              onChange={formik.handleChange}
            /> */}
            {formik.touched.purpose && formik.errors.purpose && (
              <Typography sx={{ color: "red" }}>
                {formik.errors.purpose}
              </Typography>
            )}
          </Grid>

          {/* Expense Date */}
          <Grid item xs={6} md={4}>
            <Label>Expense Date</Label>
            <DatePicker
              inputFormat="DD-MM-YYYY"
              name="collectionDate"
              value={formik.values.date}
              onChange={(e) => {
                formik.setValues((prev) => ({
                  ...prev,
                  date: e,
                }));
              }}
              maxDate={new Date()}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  placeholder="Select"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>

          {/* Expended Amount */}
          <Grid item xs={6} md={4}>
            <Label>Expended Amount</Label>
            <CustomTextField
              fullWidth
              size="small"
              placeholder="Enter Amount"
              type="number"
              value={formik.values.amt}
              name="amt"
              onChange={formik.handleChange}
            />
            {formik.touched.amt && formik.errors.amt && (
              <Typography sx={{ color: "red" }}>{formik.errors.amt}</Typography>
            )}
          </Grid>

          {/* Attachment Upload */}
          <Grid item xs={6} md={4}>
            <Label>Attachment(if any)</Label>
            <Box>
              <img
                src={
                  state?.image?.link
                    ? state?.image?.link
                    : "/image-placeholder.jpeg"
                }
                style={{
                  height: "100px",
                  width: "100px",
                  objectFit: "cover",
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
                alt="ekalsutra"
              />
            </Box>
            <Box>
              <Button variant="contained" color="secondary" component="label">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={handleFileUpload}
                />
                Upload
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Stack mt={2}>
        <PaymentModeComponent
          amount={formik.values.amt}
          formId="comp-exp-pay"
          onClickSubmit={(data) => {
            // Store payment details in ref for immediate access
            paymentDetailsRef.current = data;
            // Trigger validation and submit
            formik.handleSubmit();
          }}
        />
      </Stack>
    </>
  );
};

export default CompletedExpenseForm;
